import React, { useState, useRef } from "react";
import styles from "./FeatureCard.module.css";
import PauseIcon from "@mui/icons-material/Pause";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import Buttons from "../buttons/Buttons";

const FeatureCard = ({
  heading,
  title,
  para1,
  para2,
  para3,
  media,
  buttonText,
  buttonLink,
  newTab = false,
  reverse = false,
  darkBackground = false,
  isOdd = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [showControls, setShowControls] = useState(false);
  const videoRef = useRef(null);
  const isVideo = /\.(mp4|webm|ogg)$/i.test(media);
  // ▶️ Play / Pause
  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  // 🔇 Toggle Mute
  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  // ⏩ Change Speed
  const toggleSpeed = () => {
    if (!videoRef.current) return;
    const newSpeed = speed === 1 ? 2 : 1;
    videoRef.current.playbackRate = newSpeed;
    setSpeed(newSpeed);
  };

  // ⏲️ Time Format Helper
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${mins}:${secs}`;
  };

  return (
    <div
      className={`${styles.featureSection} ${
        darkBackground ? styles.dark : styles.light
      }`}
    >
      <div className={styles.featureSectionInner}>
        <div
          className={`${styles.featureWrapper} ${
            reverse ? styles.reverse : ""
          }`}
        >
          {/* 📝 Text Content */}
          <div
            className={`${styles.featureText} ${
              isOdd ? styles.whiteText : styles.darkText
            }`}
          >
            <h5
              className={`main_heading ${styles.HeadingUnderline} ${
                isOdd ? styles.whiteHeading : styles.orangeHeading
              }`}
            >
              {title}
            </h5>

            {heading && (
              <h5
                className={`main_subheading ${styles.featureSubHeading} ${
                  styles.HeadingUnderline
                } ${isOdd ? styles.whiteHeading : styles.orangeHeading}`}
              >
                {heading}
              </h5>
            )}

            {para1 && (
              <p
                className={`main_paragraph ${styles.featureDescription} ${
                  styles.HeadingUnderline
                } ${isOdd ? styles.whiteHeading : styles.orangeHeading}`}
              >
                {para1}
              </p>
            )}
            {para2 && (
              <p
                className={`main_paragraph ${styles.featureDescription}${
                  styles.HeadingUnderline
                } ${isOdd ? styles.whiteHeading : styles.orangeHeading}`}
              >
                {para2}
              </p>
            )}
            {para3 && (
              <p
                className={`main_paragraph ${styles.featureDescription}${
                  styles.HeadingUnderline
                } ${isOdd ? styles.whiteHeading : styles.orangeHeading}`}
              >
                {para3}
              </p>
            )}

            {buttonText && buttonLink && (
              <Buttons
                text={buttonText}
                onClick={() =>
                  newTab
                    ? window.open(buttonLink, "_blank")
                    : (window.location.href = buttonLink)
                }
              />
            )}
          </div>

          {/* 🎥 Video + Controls */}
          <div className={styles.featureImageBox}>
            <div
              className={styles.featureImageWrapper}
              onMouseEnter={() => setShowControls(true)}
              onMouseLeave={() => setShowControls(false)}
            >
              {isVideo ? (
                <video
                  ref={videoRef}
                  src={media}
                  className={styles.featureVideo}
                  controls={showControls}
                  muted
                  playsInline
                  preload="auto"
                  controlsList="nodownload noremoteplayback"
                  disablePictureInPicture
                  onEnded={() => setIsPlaying(false)}
                  onClick={handlePlayPause}
                />
              ) : (
                <img src={media} alt={title} className={styles.featureVideo} />
              )}

              {/* 🔴 Play Button */}
              {isVideo && (
                <button
                  className={`${styles.custom_play_button} ${
                    isPlaying ? styles.hideOnPlay : ""
                  }`}
                  onClick={handlePlayPause}
                >
                  {isPlaying ? (
                    <PauseIcon style={{ fontSize: "40px" }} />
                  ) : (
                    <ArrowRightIcon style={{ fontSize: "80px" }} />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureCard;
